import { showToast } from "@certn/cascadia";
import { BasicButton, Link } from "@certn/cascadia-react";
import {
  CheckMonitoredCard as SharedCheckMonitoredCard,
  type CheckMonitoredCardProps,
} from "@certn/monitoring";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { EnrollmentDetailCheck } from "../types";
import { useEnrollmentLifecycleAction } from "../useMonitoringApi";
import { MonitoringDemoResponseEditor } from "./MonitoringDemoResponseEditor";

export {
  enrollmentToRunDetail,
  MonitoringRunResultTagWithTooltip,
} from "@certn/monitoring";

interface Props {
  enrollment: EnrollmentDetailCheck;
  onActionComplete: () => void;
}

function useActionLabels(): Record<"pause" | "resume" | "renew", string> {
  const { t } = useTranslation();
  return {
    pause: t("Pause monitoring"),
    resume: t("Resume monitoring"),
    renew: t("Renew monitoring"),
  };
}

export function CheckMonitoredCard({ enrollment, onActionComplete }: Props) {
  const { t } = useTranslation();
  const runLifecycleAction = useEnrollmentLifecycleAction();
  const actionLabels = useActionLabels();
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function performAction(action: "pause" | "resume" | "renew") {
    setIsSubmitting(true);
    try {
      await runLifecycleAction(enrollment.id, action);
      showToast(t("{{action}} successful", { action: actionLabels[action] }), {
        type: "success",
      });
      onActionComplete();
    } catch {
      showToast(t("Unable to update monitoring for this check"), {
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const renderActions: CheckMonitoredCardProps["renderActions"] = () => {
    const showRenewButton =
      enrollment.monitor_status === "ENDED" && enrollment.can_renew;
    const showPauseButton = enrollment.monitor_status === "ACTIVE";
    const showResumeButton = enrollment.monitor_status === "PAUSED";

    if (!showRenewButton && !showPauseButton && !showResumeButton) {
      return null;
    }

    return (
      <>
        {showRenewButton && (
          <BasicButton
            size="small"
            onPress={() => performAction("renew")}
            isDisabled={isSubmitting}
            testID="check-renew-button"
          >
            {t("Renew monitoring")}
          </BasicButton>
        )}
        {showPauseButton && (
          <BasicButton
            size="small"
            onPress={() => performAction("pause")}
            isDisabled={isSubmitting}
            testID="check-pause-button"
          >
            {t("Pause monitoring")}
          </BasicButton>
        )}
        {showResumeButton && (
          <BasicButton
            size="small"
            onPress={() => performAction("resume")}
            isDisabled={isSubmitting}
            testID="check-resume-button"
          >
            {t("Resume monitoring")}
          </BasicButton>
        )}
      </>
    );
  };

  return (
    <SharedCheckMonitoredCard
      enrollment={enrollment}
      renderActions={renderActions}
      renderMonitorCaseLink={(shortId) => (
        <Link
          href={`/case-details/${shortId}`}
          testID="check-monitor-case-link"
        >
          {shortId}
        </Link>
      )}
      renderReferenceCaseLink={(shortId) => (
        <Link
          href={`/case-details/${shortId}`}
          testID="check-reference-case-link"
        >
          {shortId}
        </Link>
      )}
      renderDemoResponseEditor={
        enrollment.supports_demo_response
          ? (enrollmentId) => (
              <MonitoringDemoResponseEditor enrollmentId={enrollmentId} />
            )
          : undefined
      }
    />
  );
}
